import { equals, head, includes, keys, map, not, pathOr, test } from 'ramda';
import { reportException } from 'src/exceptionReporting';
import { getNamespaceName } from 'src/services/objects/namespaces';
import { isEmptyOrNil, isNotEmpty } from './hasValue';
import { USER_ONBOARD_APPLICANT } from 'src/services/constants/accounts';
import { base64Decode } from './base64';
import { EXISTING_IN_BRANCH_CUSTOMER, NEW_IN_BRANCH_CUSTOMER } from 'src/services/constants';
import { generateUUID } from 'pubnub';

const localStorageCache: any = {};
export const getStorage = (key: string, fallback?: any) => {
  if (localStorageCache[key]) {
    return localStorageCache[key];
  }

  const item = window.sessionStorage.getItem(key);
  /*
   * Basically, if localstorage doesn't exist,
   * return whatever we set as a fallback
   */
  if ((item === null || item === undefined) && !!fallback) {
    return fallback;
  }

  try {
    // Try to parse as JSON first. This will turn "true" (string) into `true` (boolean).
    const parsedItem = parseData(item);

    localStorageCache[key] = parsedItem;
    return parsedItem;
  } catch (e) {
    reportException(`<----storage-29----> ${e}`);
    // It's okay if we can't parse as JSON -- just use the raw value instead.
    localStorageCache[key] = item;
    return item;
  }
};



export const setStorage = (key: string, value: string) => {
  try {
    // Store parsed JSON if possible.
    localStorageCache[key] = parseData(value);
  } catch (e) {
    reportException(`<----storage-41----> ${e}`);
    // Otherwise just use the raw value.
    localStorageCache[key] = value;
  }
  return window.sessionStorage.setItem(key, value);
};

export const setObjectStorage = (key: string, value: any) => {
  return window.sessionStorage.setItem(key, JSON.stringify(value));
};
export const setStorageAsString = (key: string, value: any) => {
  return window.sessionStorage.setItem(key, value);
};

const removeFromStorage = (key: string) => {
  localStorageCache[key] = undefined
  return window.sessionStorage.removeItem(key);
};
export const getStorageAsString = (key: string,defaultValue?:any) => {
  const item = window.sessionStorage.getItem(key);

  return defaultValue ? defaultValue : equals(item, 'null') ? null : item;
};


const setLocalStorageAsString = (key: string, value: any) => {
  return localStorage.setItem(key, value);
};

const removeFromLocalStorage = (key: string) => {
  return localStorage.removeItem(key);
};

const getLocalStorageAsString = (key: string) => {
  const item = localStorage.getItem(key) || 'null'

  return equals(item, 'null') ? null : item;
};



/**
 * The general local storage keys and their descrption
 *
 * prefixes a namspace to the key
 *
 *
 * @param key { string } The key to prefix.
 *
 */

const prefixNamespace = (key: string) => `${getNamespaceName()}_${key}`;
const prefixAuthLabel = (key: string) => prefixNamespace(`authentication/${key}`);
const prefixNoAuthLabel = (key: string) => prefixNamespace(`unauthentication/${key}`);

/////////// KEYS
const PAGE_SIZE = prefixNamespace('PAGE_SIZE');

/**
 * The storage keys with regards to a token
 *
 * <namespace>_authentication/auth-token: This stores the user session auth token for core api
 *
 */
// authentication
const AUTH_TOKEN = prefixAuthLabel('auth-token');
const AUTH_EXPIRE_DATETIME = prefixAuthLabel('expire-datetime');
const LATEST_REFRESH = prefixAuthLabel('latest-refresh');
const AUTH_LOGIN_TIME = prefixAuthLabel('login-time');
const AUTH_IN_ACTIVE_TIME = prefixAuthLabel('inactive-time');
const RECENT_NOTIFICATION_ID = prefixAuthLabel('recent-notification-id');

/**
 * Local storage keys
 */
export const LOCAL_STORAGE_USER_AUTH_SESSION = `${getNamespaceName()}_user`;
const LOCAL_STORAGE_REMEMBER_USER_AUTH_SESSION = `${getNamespaceName()}_user_remember`;


/**
 * The storage keys with regards to an user
 *
 * <namespace>_authentication/account-id : stores the account id for the namespace
 * <namespace>_authentication/account-email: stores the email of the authenticated user
 * <namespace>_authentication/account-name: stores the email of the authenticated user
 * <namespace>_authentication/loggedin-role: stores the loggedin role of an
 * authenticated user
 *
 */
// authenticatedUser
const AUTH_ACCOUNT_ID = prefixAuthLabel('account-id');
const AUTH_ACCOUNT_EMAIL = prefixAuthLabel('account-email');
const AUTH_ACCOUNT_TIMEZONE = prefixAuthLabel('account-timezone');
const AUTH_ACCOUNT_NAME = prefixAuthLabel('account-name');
const AUTH_LOGGED_IN_ROLE = prefixAuthLabel('loggedin-role');
const AUTH_ACCOUNT_ROLES = prefixAuthLabel('account-roles');
const APPLICANT_USER_CONTEXT = prefixAuthLabel('applicant-user-context');
const STORAGE_KEY = 'site/cookie_consent';

const AUTH_TEAM_ID = prefixAuthLabel('team-id');

const REDIRECT_PATH = prefixNamespace('redirect-path');

const NO_AUTH_LOAN_TYPE = prefixNoAuthLabel('loan-type');
const NO_AUTH_LOAN_DATA = prefixNoAuthLabel('encoded-loan-data');
const NO_AUTH_ADVISOR_USERNAME = prefixNoAuthLabel(
  'advisor-username'
);
const NO_AUTH_CAMPAIGN_TYPE = prefixNoAuthLabel(
  'campaign'
);
const NO_EXISTING_APPS = prefixNoAuthLabel(
  'exisistingApps'
);
const NO_AUTH_SIGNUP_TYPE = prefixNoAuthLabel('signup-type');
const NO_AUTH_USER_TYPE = prefixNoAuthLabel('user-type');
const NO_AUTH_ADVISOR_PRODUCT_TYPE = prefixNoAuthLabel(
  'product-type'
);
const NO_AUTH_PRODUCT_ALIAS_NAME = prefixNoAuthLabel(
  'alias-name'
);
const NO_AUTH_LEAD_UID = prefixNoAuthLabel('lead-uid');
const NO_AUTH_AUTHORIZED_USER_ID = prefixNoAuthLabel('authorized-user-uid');
const NO_AUTH_WORKFLOW_INTENT = prefixNoAuthLabel('workflow-intent');
const NO_AUTH_RENEWAL = prefixNoAuthLabel('renewal');
const NO_AUTH_INVITE_PARAMS = prefixNoAuthLabel('invite-params');
const NO_AUTH_WORKFLOW_AS = prefixNoAuthLabel('workflow-started-as');
const NO_AUTH_SESSION_EMPLOYMENT = prefixNoAuthLabel('employments');
const NO_AUTH_SESSION_DECLARATIONS = prefixNoAuthLabel('declarations');
const NO_AUTH_SESSION_BORROWER = prefixNoAuthLabel('borrowers');
const NO_AUTH_SESSION_LENDING_TERM = prefixNoAuthLabel('lendingTerm');
const NO_AUTH_SESSION_PROPERTY = prefixNoAuthLabel('property');
const NO_AUTH_SESSION_BUSINESS = prefixNoAuthLabel('business');
const NO_AUTH_SESSION_MONTHLY_INCOMES = prefixNoAuthLabel('monthly_incomes');
const NO_AUTH_SESSION_REALESTATE_ASSETS = prefixNoAuthLabel('realestate_assets');
const NO_AUTH_SESSION_ASSETS = prefixNoAuthLabel('assets');
const NO_AUTH_SESSION_LIABILITIES = prefixNoAuthLabel('liabilites');
const NO_AUTH_SESSION_COLLATERALS = prefixNoAuthLabel('collaterals');



/**
 * The storage keys with regards to an user'd context
 *
 * <namespace>_authentication/context : stores the users context (loans/their
 * loan role/teams)
 * <namespace>_authentication/current-context: stores the current context of an user
 * DEPRECATED:? <namespace>_authentication/account-name: stores the current account-id.
 * don't know what the heck current account id means
 * <namespace>_authentication/team-id: team id of the user logged in
 * <namespace>_authentication/advisor-id: used for id of the advisor who logged in
 * *
 */
// loan
const AUTH_CONTEXT = prefixAuthLabel('context');
const AUTH_CURRENT_CONTEXT = prefixAuthLabel('current-context');
const TASK_PROPS = prefixAuthLabel('task-props');

const AUTH_ADVISOR_ID = prefixAuthLabel('advisor-id');

// devtools ENV
const DEV_TOOLS_ENV = 'devTools/env';

export type PageSize = number;

export interface DevToolsEnv {
  apiRoot: string;
  loginRoot: string;
  clientID: string;
  label: string;
}

interface AuthGetAndSet {
  get: () => any;
  set: (value: any) => void;
  remove: () => any;
}

export interface Storage {
  webSite: {
    cookieConsent: AuthGetAndSet;
  }
  authentication: {
    user: AuthGetAndSet;
    rememberMe: AuthGetAndSet;
    token: AuthGetAndSet;
    expire: AuthGetAndSet;
    latestRefresh: AuthGetAndSet;
    loginTime: AuthGetAndSet;
    inActiveTime: AuthGetAndSet;
  };

  pageSize: {
    get: () => PageSize;
    set: (perPage: PageSize) => void;
    remove: () => void
  };
  // authenticatedUser
  authenticatedUser: {
    accountId: AuthGetAndSet;
    accountEmail: AuthGetAndSet;
    accountTimeZone: AuthGetAndSet;
    accountName: AuthGetAndSet;
    loggedInRole: AuthGetAndSet;
    rolesId: AuthGetAndSet;
    teamId: AuthGetAndSet;
    recentNotificationId: AuthGetAndSet

  };
  unAuthenticatedUser: {
    authorizedUserID: AuthGetAndSet;
    leadUID: AuthGetAndSet;
    workFlowIntent: AuthGetAndSet;
    inviteParams: AuthGetAndSet;
    workflowStartedAs: AuthGetAndSet;
    renewal: AuthGetAndSet;
    advisorUsername: AuthGetAndSet;
    productType: AuthGetAndSet;
    aliasName: AuthGetAndSet;
    loanType: AuthGetAndSet;
    signupType: AuthGetAndSet;
    userType: AuthGetAndSet;
    loanData: AuthGetAndSet;
    campaign: AuthGetAndSet;

  }
  // current loan context of the application workflow
  authenticatedApplicantContext: {
    // We should use this applicantContext to maintain loan's primary applicant's account details
    applicantContext: AuthGetAndSet;
    currentLoanRoleContext: AuthGetAndSet;
    loansContext: AuthGetAndSet;
    taskProps: AuthGetAndSet;
    existingApps: AuthGetAndSet;

  };

  unAuthenticatedLoan: {
    employments: AuthGetAndSet;
    borrowers: AuthGetAndSet;
    declarations: AuthGetAndSet;
    lendingTerm: AuthGetAndSet;
    business: AuthGetAndSet;
    monthlyIncomes: AuthGetAndSet;
    realEstateAssets: AuthGetAndSet;
    collaterals: AuthGetAndSet;
    assets: AuthGetAndSet;
    liabilities: AuthGetAndSet;
    property: AuthGetAndSet;

  };
  authenticatedAdvisor: {
    advisorId: AuthGetAndSet;
  };
  devToolsEnv: {
    get: () => DevToolsEnv | null;
    set: (devToolsEnv: DevToolsEnv) => void;
    remove: () => void;

  };
  //Any redirect path in the mailers will have the information saved in session.
  savedRedirectPath: AuthGetAndSet;
}

/*The current context will have the current loan we operate on
   * {
            "loanapp_id": "1756953223691714560",
            "loanrole_id": [
                "1756953223750074368"
            ],
            'account_id':"1756951470865686787"
            "team_id": "1756951470865686528",
            "type_of_loan": "residentialmortgage"
        }
    */
export const storage: Storage = {
  webSite: {
    cookieConsent: {
      get: () => getLocalStorageAsString(STORAGE_KEY),
      set: (v) => setLocalStorageAsString(STORAGE_KEY, v),
      remove: () => removeFromLocalStorage(STORAGE_KEY)
    }
  },
  authentication: {
    // Browser Local storage 
    user: {
      get: () => getLocalStorageAsString(LOCAL_STORAGE_USER_AUTH_SESSION),
      set: (v) => setLocalStorageAsString(LOCAL_STORAGE_USER_AUTH_SESSION, v),
      remove: () => removeFromLocalStorage(LOCAL_STORAGE_USER_AUTH_SESSION)
    },
    rememberMe: {
      get: () => getLocalStorageAsString(LOCAL_STORAGE_REMEMBER_USER_AUTH_SESSION),
      set: (v) => setLocalStorageAsString(LOCAL_STORAGE_REMEMBER_USER_AUTH_SESSION, v),
      remove: () => removeFromLocalStorage(LOCAL_STORAGE_REMEMBER_USER_AUTH_SESSION)
    },
    // tab Session storage 
    token: {
      get: () => getStorageAsString(AUTH_TOKEN),
      set: (v) => setStorageAsString(AUTH_TOKEN, v),
      remove: () => removeFromStorage(AUTH_TOKEN)
    },
    expire: {
      get: () => getStorageAsString(AUTH_EXPIRE_DATETIME),
      set: (v) => setStorageAsString(AUTH_EXPIRE_DATETIME, v),
      remove: () => removeFromStorage(AUTH_EXPIRE_DATETIME)

    },
    latestRefresh: {
      get: () => getStorageAsString(LATEST_REFRESH),
      set: (v) => setStorageAsString(LATEST_REFRESH, v),
      remove: () => removeFromStorage(LATEST_REFRESH)

    },
    loginTime: {
      get: () => getStorageAsString(AUTH_LOGIN_TIME),
      set: (v) => setStorageAsString(AUTH_LOGIN_TIME, v),
      remove: () => removeFromStorage(AUTH_LOGIN_TIME)

    },
    inActiveTime: {
      get: () => getStorageAsString(AUTH_IN_ACTIVE_TIME),
      set: (v) => setStorageAsString(AUTH_IN_ACTIVE_TIME, v),
      remove: () => removeFromStorage(AUTH_IN_ACTIVE_TIME)

    }
  },
  pageSize: {
    get: () => Number(getStorageAsString(PAGE_SIZE)),
    set: (v) => setStorageAsString(PAGE_SIZE, `${v}`),
    remove: () => removeFromStorage(PAGE_SIZE)
  },
  unAuthenticatedUser: {
    renewal: {
      get: () => getStorage(NO_AUTH_RENEWAL),
      set: (v) => setStorage(NO_AUTH_RENEWAL, v),
      remove: () => removeFromStorage(NO_AUTH_RENEWAL)
    },
    workFlowIntent: {
      get: () => getStorage(NO_AUTH_WORKFLOW_INTENT),
      set: (v) => setStorage(NO_AUTH_WORKFLOW_INTENT, v),
      remove: () => removeFromStorage(NO_AUTH_WORKFLOW_INTENT)
    },
    leadUID: {
      get: () => getStorageAsString(NO_AUTH_LEAD_UID),
      set: (v) => setStorageAsString(NO_AUTH_LEAD_UID, v),
      remove: () => removeFromStorage(NO_AUTH_LEAD_UID)

    },
    authorizedUserID: {
      get: () => getStorageAsString(NO_AUTH_AUTHORIZED_USER_ID, generateUUID()),
      set: (v) => setStorageAsString(NO_AUTH_AUTHORIZED_USER_ID, v),
      remove: () => removeFromStorage(NO_AUTH_AUTHORIZED_USER_ID)
    },
    inviteParams: {
      get: () => getStorage(NO_AUTH_INVITE_PARAMS),
      set: (v) => setStorage(NO_AUTH_INVITE_PARAMS, v),
      remove: () => removeFromStorage(NO_AUTH_INVITE_PARAMS)

    },
    workflowStartedAs: {
      get: () => getStorage(NO_AUTH_WORKFLOW_AS),
      set: (v) => setStorage(NO_AUTH_WORKFLOW_AS, v),
      remove: () => removeFromStorage(NO_AUTH_WORKFLOW_AS)
    },
    advisorUsername: {
      get: () => getLocalStorageAsString(NO_AUTH_ADVISOR_USERNAME),
      set: (v) => setLocalStorageAsString(NO_AUTH_ADVISOR_USERNAME, v),
      remove: () => removeFromLocalStorage(NO_AUTH_ADVISOR_USERNAME)
    },
    campaign: {
      get: () => getStorageAsString(NO_AUTH_CAMPAIGN_TYPE),
      set: (v) => setStorageAsString(NO_AUTH_CAMPAIGN_TYPE, v),
      remove: () => removeFromStorage(NO_AUTH_CAMPAIGN_TYPE)

    },
    productType: {
      get: () => getStorageAsString(NO_AUTH_ADVISOR_PRODUCT_TYPE),
      set: (v) => setStorageAsString(NO_AUTH_ADVISOR_PRODUCT_TYPE, v),
      remove: () => removeFromStorage(NO_AUTH_ADVISOR_PRODUCT_TYPE)

    },
    aliasName: {
      get: () => getStorageAsString(NO_AUTH_PRODUCT_ALIAS_NAME),
      set: (v) => setStorageAsString(NO_AUTH_PRODUCT_ALIAS_NAME, v),
      remove: () => removeFromStorage(NO_AUTH_PRODUCT_ALIAS_NAME)

    },
    signupType: {
      get: () => getStorageAsString(NO_AUTH_SIGNUP_TYPE),
      set: (v) => setStorageAsString(NO_AUTH_SIGNUP_TYPE, v),
      remove: () => removeFromStorage(NO_AUTH_SIGNUP_TYPE)

    },
    userType: {
      get: () => getStorageAsString(NO_AUTH_USER_TYPE),
      set: (v) => setStorageAsString(NO_AUTH_USER_TYPE, v),
      remove: () => removeFromStorage(NO_AUTH_USER_TYPE)

    },
    loanType: {
      get: () => getStorageAsString(NO_AUTH_LOAN_TYPE),
      set: (v) => setStorageAsString(NO_AUTH_LOAN_TYPE, v),
      remove: () => removeFromStorage(NO_AUTH_LOAN_TYPE)

    },
    loanData: {
      get: () => getStorage(NO_AUTH_LOAN_DATA),
      set: (v) => setStorage(NO_AUTH_LOAN_DATA, v),
      remove: () => removeFromStorage(NO_AUTH_LOAN_DATA)

    },
  },
  authenticatedUser: {
    accountId: {
      get: () => getStorageAsString(AUTH_ACCOUNT_ID),
      set: (v) => setStorageAsString(AUTH_ACCOUNT_ID, v),
      remove: () => removeFromStorage(AUTH_ACCOUNT_ID)

    },
    accountEmail: {
      get: () => getStorageAsString(AUTH_ACCOUNT_EMAIL),
      set: (v) => setStorageAsString(AUTH_ACCOUNT_EMAIL, v),
      remove: () => removeFromStorage(AUTH_ACCOUNT_EMAIL)
    },
    accountTimeZone: {
      get: () => getStorageAsString(AUTH_ACCOUNT_TIMEZONE),
      set: (v) => setStorageAsString(AUTH_ACCOUNT_TIMEZONE, v),
      remove: () => removeFromStorage(AUTH_ACCOUNT_TIMEZONE)

    },
    accountName: {
      get: () => getStorageAsString(AUTH_ACCOUNT_NAME),
      set: (v) => setStorageAsString(AUTH_ACCOUNT_NAME, v),
      remove: () => removeFromStorage(AUTH_ACCOUNT_NAME)

    },
    loggedInRole: {
      get: () => getStorageAsString(AUTH_LOGGED_IN_ROLE),
      set: (v) => setStorageAsString(AUTH_LOGGED_IN_ROLE, v),
      remove: () => removeFromStorage(AUTH_LOGGED_IN_ROLE)

    },
    rolesId: {
      get: () => getStorageAsString(AUTH_ACCOUNT_ROLES),
      set: (v) => setStorageAsString(AUTH_ACCOUNT_ROLES, v),
      remove: () => removeFromStorage(AUTH_ACCOUNT_ROLES)
    },
    teamId: {
      get: () => getStorageAsString(AUTH_TEAM_ID),
      set: (v) => setStorageAsString(AUTH_TEAM_ID, v),
      remove: () => removeFromStorage(AUTH_TEAM_ID)
    },
    recentNotificationId: {
      get: () => getStorageAsString(RECENT_NOTIFICATION_ID),
      set: (v) => setStorageAsString(RECENT_NOTIFICATION_ID, v),
      remove: () => removeFromStorage(RECENT_NOTIFICATION_ID)

    }
  },
  unAuthenticatedLoan: {
    employments: {
      get: () => getStorage(NO_AUTH_SESSION_EMPLOYMENT),
      set: (v: any) => setStorage(NO_AUTH_SESSION_EMPLOYMENT, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_EMPLOYMENT)

    },
    lendingTerm: {
      get: () => getStorage(NO_AUTH_SESSION_LENDING_TERM),
      set: (v: any) => setStorage(NO_AUTH_SESSION_LENDING_TERM, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_LENDING_TERM)

    },
    property: {
      get: () => getStorage(NO_AUTH_SESSION_PROPERTY),
      set: (v: any) => setStorage(NO_AUTH_SESSION_PROPERTY, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_PROPERTY)

    },
    business: {
      get: () => getStorage(NO_AUTH_SESSION_BUSINESS),
      set: (v: any) => setStorage(NO_AUTH_SESSION_BUSINESS, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_BUSINESS)

    },
    monthlyIncomes: {
      get: () => getStorage(NO_AUTH_SESSION_MONTHLY_INCOMES),
      set: (v: any) => setStorage(NO_AUTH_SESSION_MONTHLY_INCOMES, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_MONTHLY_INCOMES)

    },
    realEstateAssets: {
      get: () => getStorage(NO_AUTH_SESSION_REALESTATE_ASSETS),
      set: (v: any) => setStorage(NO_AUTH_SESSION_REALESTATE_ASSETS, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_REALESTATE_ASSETS)

    },
    collaterals: {
      get: () => getStorage(NO_AUTH_SESSION_COLLATERALS),
      set: (v: any) => setStorage(NO_AUTH_SESSION_COLLATERALS, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_COLLATERALS)

    },
    assets: {
      get: () => getStorage(NO_AUTH_SESSION_ASSETS),
      set: (v: any) => setStorage(NO_AUTH_SESSION_ASSETS, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_ASSETS)

    },
    liabilities: {
      get: () => getStorage(NO_AUTH_SESSION_LIABILITIES),
      set: (v: any) => setStorage(NO_AUTH_SESSION_LIABILITIES, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_LIABILITIES)

    },
    borrowers: {
      get: () => getStorage(NO_AUTH_SESSION_BORROWER),
      set: (v: any) => setStorage(NO_AUTH_SESSION_BORROWER, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_BORROWER)

    },
    declarations: {
      get: () => getStorage(NO_AUTH_SESSION_DECLARATIONS),
      set: (v: any) => setStorage(NO_AUTH_SESSION_DECLARATIONS, v),
      remove: () => removeFromStorage(NO_AUTH_SESSION_DECLARATIONS)

    }

  },
  authenticatedApplicantContext: {
    loansContext: {
      get: () => getStorageAsString(AUTH_CONTEXT),
      set: (v) => setStorageAsString(AUTH_CONTEXT, v),
      remove: () => removeFromStorage(AUTH_CONTEXT)
    },
    applicantContext: {
      get: () => getStorageAsString(APPLICANT_USER_CONTEXT),
      set: (v) => setStorageAsString(APPLICANT_USER_CONTEXT, v),
      remove: () => removeFromStorage(APPLICANT_USER_CONTEXT)
    },
    currentLoanRoleContext: {
      get: () => getStorageAsString(AUTH_CURRENT_CONTEXT),
      set: (v: any) => setStorageAsString(AUTH_CURRENT_CONTEXT, v),
      remove: () => removeFromStorage(AUTH_CURRENT_CONTEXT)

    },
    taskProps: {
      get: () => getStorageAsString(TASK_PROPS),
      set: (v: any) => setStorageAsString(TASK_PROPS, v),
      remove: () => removeFromStorage(TASK_PROPS)

    },
    existingApps: {
      get: () => getStorageAsString(NO_EXISTING_APPS),
      set: (v) => setStorageAsString(NO_EXISTING_APPS, v),
      remove: () => removeFromStorage(NO_EXISTING_APPS)

    },
  },
  authenticatedAdvisor: {
    advisorId: {
      get: () => getStorageAsString(AUTH_ADVISOR_ID),
      set: (v) => setStorageAsString(AUTH_ADVISOR_ID, v),
      remove: () => removeFromStorage(AUTH_ADVISOR_ID)

    },
  },
  devToolsEnv: {
    get: () => {
      const value = getStorage(DEV_TOOLS_ENV);
      return isDevToolsEnvValid(value) ? value : undefined;
    },
    set: (devToolsEnv) =>
      setStorage(DEV_TOOLS_ENV, JSON.stringify(devToolsEnv)),
    remove: () => removeFromStorage(DEV_TOOLS_ENV)
  },
  savedRedirectPath: {
    get: () => getStorageAsString(REDIRECT_PATH),
    set: (v) => setStorageAsString(REDIRECT_PATH, v),
    remove: () => removeFromStorage(REDIRECT_PATH)

  }
};

export const {
  authentication,
  authenticatedApplicantContext,
  authenticatedUser,
  authenticatedAdvisor,
  savedRedirectPath,
  unAuthenticatedUser,
  unAuthenticatedLoan,
  webSite
} = storage;


/**
 * Clears the specified storages and skip the specified keys.
 *
 * @param {object} storages - The storages to clear.
 * @param {array} [keysToBeSkipped] - The keys to skip when clearing the storage.
 * @returns {object} The updated storages.
 */

export const clearStorage = (storages: any, keysToBeSkipped: string[] = []) => {
  return map((storage) => {
    return map((key: string) => {
      const shouldSkip = isNotEmpty(keysToBeSkipped) && includes(key, keysToBeSkipped)
      const currentStorage: any = pathOr({}, [key], storage)
      return shouldSkip ? {} : currentStorage?.remove()
    }, keys(storage))
  }, storages)
}



/// Only return these if the dev tools are enabled and we're in development mode.
export const getEnvLocalStorageOverrides = () => {
  // This is broken into two logical branches so that local storage is accessed
  // ONLY if the dev tools are enabled and it's a development build.
  if (process.env.NODE_ENV === 'development') {
    const localStorageOverrides = storage.devToolsEnv.get();
    if (localStorageOverrides) {
      return localStorageOverrides;
    }
  }
  return undefined;
};

export const isDevToolsEnvValid = (value: any) => {
  return (
    typeof value?.apiRoot === 'string' &&
    typeof value?.loginRoot === 'string' &&
    typeof value?.clientID === 'string' &&
    typeof value?.label === 'string'
  );
};

export const isUserBeingRemembered = () => {
  const sessionUser = authentication.user.get()
  const rememberMe = authentication.rememberMe.get()
  if (isEmptyOrNil(sessionUser) || isEmptyOrNil(rememberMe)) {
    return false
  }
  return equals(rememberMe, 'true')
}


export const isRememberingUserLender = () => {
  if (!isUserBeingRemembered()) {
    return false
  }
  const sessionUser = authentication.user.get()
  const userDetails = base64Decode(sessionUser)
  const rolesId = pathOr([], ['roles_id'], userDetails)
  return not(includes(USER_ONBOARD_APPLICANT, rolesId))
}

export const isAuthenticatedLender = () => {
  const roles = authenticatedUser.rolesId.get()
  const rolesId = isNotEmpty(roles) ? JSON.parse(`${roles}`) : []
  const isAuthenticatedBanker = isNotEmpty(authenticatedUser.accountEmail.get()) &&
    isNotEmpty(rolesId) && not(includes(USER_ONBOARD_APPLICANT, rolesId))
  return isAuthenticatedBanker
}

export const primaryApplicantContext = () => {
  const encoedAccountContext = authenticatedApplicantContext.applicantContext.get();
  const accountContext = base64Decode(encoedAccountContext);

  const {
    accountId,
    accountEmail,
    rolesId,
    teamId
  } = accountContext;

  return {
    accountId,
    accountEmail,
    rolesId,
    teamId
  }
}

/**
 * currentLoanRoleContext
 *
 * Destructures and provides a single context in camelCase values
 *
 * @param context { LoanContext } LoanContext from the reducer
 * Example: {
            "loanapp_id": "1756953223691714560",
            "loanrole_id": [
                "1756953223750074368"
            ],
            'account_id':"1756951470865686787"
            "team_id": "1756951470865686528",
            "type_of_loan": "residentialmortgage"
        }
   If we notice the loanrole_id is an array. Hence we select the head.
 **/
export const currentAppSession = () => {
  const encoedCurrentContext = authenticatedApplicantContext.currentLoanRoleContext.get();
  const decoded = decodeURIComponent(window.atob(encoedCurrentContext));
  const currentLoanRoleContext = encoedCurrentContext ? parseData(decoded) : {};
  const loanAppId = pathOr('', ['loanAppId'], currentLoanRoleContext);
  const loanType = pathOr('', ['loanType'], currentLoanRoleContext);
  const loanProductType = pathOr('', ['loanProductType'], currentLoanRoleContext);
  const loanRoleIds = pathOr([], ['loanRoleIds'], currentLoanRoleContext);
  const teamId =
    pathOr('', ['teamId'], currentLoanRoleContext) || authenticatedUser.teamId.get();
  const loanRoleAccountId = pathOr('', ['loanRoleAccountId'], currentLoanRoleContext);
  const accountEmail = pathOr('', ['accountEmail'], currentLoanRoleContext);
  const hasCurrentContext = pathOr(
    false,
    ['hasCurrentContext'],
    currentLoanRoleContext
  );

  return {
    loanAppId,
    loanRoleId: head(loanRoleIds) || '',
    loanRoleAccountId,
    teamId,
    loanType,
    loanProductType,
    accountEmail,
    hasCurrentContext,
  };
};

export const setLoginRole = (e: any) => {
  authenticatedUser.loggedInRole.set(e);
};

export const parseData = (data: any) => {
  const regex = /^\s*(\{.*\}|\[.*\])\s*$/;
  try {
    return isNotEmpty(data) && test(regex, data) ? JSON.parse(data) : data;
  } catch (error: any) {
    return reportException(`<---storage-498----> ${error}`);
  }
};


export const isAnsweringFor3rdPerson = () => {
  const { loanRoleAccountId } = currentAppSession();
  return loanRoleAccountId && not(equals(loanRoleAccountId, authenticatedUser.accountId.get()))
}

export const getInbranchType = () => {
  const workflowStartedAs = unAuthenticatedUser.workflowStartedAs.get();
  const { customer }: any = base64Decode(workflowStartedAs)
  return customer
}
export const isExistingInBranchCustomer = () => {

  return equals(getInbranchType(), EXISTING_IN_BRANCH_CUSTOMER);
}


export const isNewInBranchCustomer = () => {

  return equals(getInbranchType(), NEW_IN_BRANCH_CUSTOMER);
}

export const isInBranchWorkflow = (): boolean => {
  return isNotEmpty(getInbranchType())
}

export const isCookieRejected = (): boolean => {
  const cookieConsent = webSite.cookieConsent.get();
  try {
    const { value }: any = JSON.parse(cookieConsent)
    return equals(value, 'rejected')
  } catch {
  }
  return false
}

export const isCookieAccepted = (): boolean => {
  const cookieConsent = webSite.cookieConsent.get();
  try {
    const { value }: any = JSON.parse(cookieConsent)
    return equals(value, 'accepted')
  } catch {
  }
  return false
}


